import type { Ref } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { ContextValue } from 'react-aria-components/slots';
import type { SwitchProps as AriaSwitchProps } from 'react-aria-components/Switch';
import { Switch as AriaSwitch } from 'react-aria-components/Switch';
import { TooltipTriggerStateContext } from 'react-aria-components/Tooltip';
import { mergeRefs } from '@react-aria/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { cancelClick, TooltipTriggerContext, useIsDisabledTooltipTrigger } from './Perceivable';
import { useLPContextProps } from './utils';

import styles from './styles/Switch.module.css';

const switchStyles = cva(styles.switch, {
	variants: {
		variant: {
			default: '',
			primary: styles.primary,
		},
		compact: {
			true: styles.compact,
		},
		isDisabledTrigger: {
			true: styles.disabled,
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

interface SwitchProps
	extends AriaSwitchProps, Omit<VariantProps<typeof switchStyles>, 'compact' | 'isDisabledTrigger'> {
	ref?: Ref<HTMLLabelElement>;
	/** Pass `false` to hide On/Off labels and render a compact track. */
	switchLabels?: false;
}

const SwitchContext = createContext<ContextValue<SwitchProps, HTMLLabelElement>>(null);

/**
 * A switch allows a user to turn a setting on or off.
 *
 * Provide an accessible name via `children` (visible label) or `aria-label` (visually hidden label).
 *
 * A disabled switch placed directly in a `TooltipTrigger` becomes read-only instead, so it stays focusable and
 * hoverable and the tooltip can explain why it can't be changed. It is announced as read-only.
 *
 * https://react-spectrum.adobe.com/react-aria/Switch.html
 */
const Switch = ({ ref, ...props }: SwitchProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, SwitchContext);
	const { switchLabels, variant } = mergedProps;
	const hideLabels = switchLabels === false ? true : undefined;
	const isDisabledTrigger = useIsDisabledTooltipTrigger(mergedProps.isDisabled);
	const tooltipTrigger = useContext(TooltipTriggerContext);
	const tooltipState = useContext(TooltipTriggerStateContext);
	const setAnchor = isDisabledTrigger ? tooltipTrigger?.setAnchor : undefined;
	const switchRef = useMemo(
		() => (setAnchor ? mergeRefs<HTMLLabelElement>(mergedRef, setAnchor) : mergedRef),
		[mergedRef, setAnchor],
	);
	const disabledTriggerProps = isDisabledTrigger && {
		isDisabled: false,
		isReadOnly: true,
		onClickCapture: cancelClick,
		onPointerEnter: () => tooltipState?.open(),
		onPointerLeave: () => tooltipState?.close(),
	};

	return (
		<AriaSwitch
			{...mergedProps}
			{...disabledTriggerProps}
			ref={switchRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				switchStyles({ ...renderProps, variant, compact: hideLabels, isDisabledTrigger, className }),
			)}
		>
			{composeRenderProps(mergedProps.children, (children, { isSelected }) => (
				<>
					<div className={styles.track}>
						{!hideLabels && isSelected && <div className={styles.label}>On</div>}
						<span className={styles.handle} />
						{!hideLabels && !isSelected && <div className={styles.label}>Off</div>}
					</div>
					{children}
				</>
			))}
		</AriaSwitch>
	);
};

export { Switch, SwitchContext, switchStyles };
export type { SwitchProps };
